import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import type { AssetFields } from 'src/types/asset';
import { useState } from 'react';
import { pick } from 'es-toolkit';
import ItemDialogBackButton from 'src/components/mui/item-dialog-back-button';
import ItemDialogYesNoButtons from 'src/components/mui/item-dialog-yes-no-buttons';
import ItemHeader from 'src/components/portfolio/views/item-header';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { removeFalsey } from 'src/utils/object-manipulation';

import type { CreateAssetInput, UpdateAssetInput } from '@savvy/constants/generated/gql-types';
import { ASSET_CLASS_COMPANY_EQUITY } from '@savvy/constants/asset-classes';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import CompanyEquityBasicsForm from './company-equity-basics-form';
import CompanyEquityGrantDetailsForm from './company-equity-grant-details-form';
import CompanyEquityValueForm from './company-equity-value-form';

export const AddEditCompanyEquityFragment = graphql(/* GraphQL */ `
  fragment AddEditCompanyEquity on Asset {
    ...CompanyEquityBasicsForm
    ...CompanyEquityGrantDetailsForm
    ...CompanyEquityValueForm
    id
    companyEquityExercisePrice
    companyEquityGrantType
    companyEquitySharesIssued
    companyEquityVestingCliff
    companyEquityVestingDuration
    companyEquityVestingRate
    companyEquityVestingStartDate
  }
`);

const enum FlowStep {
  CompanyEquityBasics = 'CompanyEquityBasics',
  GrantDetails = 'GrantDetails',
  EquityValue = 'EquityValue',
}

const enum GrantDetailsState {
  UNKNOWN = 'undefined',
  YES = 'true',
  NO = 'false',
}

type CompanyEquityAssetPayloadSource = AssetFields &
  Partial<Pick<UpdateAssetInput, 'assetClass' | 'assetSubclass' | 'clientMutationId' | 'id'>>;
type CompanyEquityCreateAssetInput = Omit<CreateAssetInput, 'currentValue'> & {
  readonly currentValue?: CreateAssetInput['currentValue'] | null;
};
type CompanyEquityUpdateAssetInput = Omit<UpdateAssetInput, 'id'> & Partial<Pick<UpdateAssetInput, 'id'>>;
type CompanyEquityGrantFields = Pick<
  AssetFields,
  | 'companyEquityExercisePrice'
  | 'companyEquityGrantType'
  | 'companyEquitySharesIssued'
  | 'companyEquityVestingCliff'
  | 'companyEquityVestingDuration'
  | 'companyEquityVestingRate'
  | 'companyEquityVestingStartDate'
>;

const CREATE_ASSET = graphql(/* GraphQL */ `
  mutation CreateAsset($input: CreateAssetInput!) {
    createAsset(input: $input) {
      asset {
        id
      }
    }
  }
`);

const UPDATE_ASSET = graphql(/* GraphQL */ `
  mutation UpdateAsset($input: UpdateAssetInput!) {
    updateAsset(input: $input) {
      asset {
        id
        description
        assetClass
        status
        assetSubclass
        costBasis
        hiddenFromClient
        hiddenFromNetWorth
        lastUpdatedAt
        companyEquityCompanyName
        companyEquityCompanyTicker
        companyEquityCurrentPrice
        companyEquityGrantType
        companyEquitySharesIssued
        companyEquityVestingStartDate
        companyEquityVestingDuration
        companyEquityVestingCliff
        companyEquityVestingRate
        companyEquityExercisePrice
        liabilities {
          id
          name
          type
          currentBalance {
            amount
            currencyCode
          }
          originalBalance {
            amount
            currencyCode
          }
          providerName
          acquiredAt
          timeToMaturity
          timeToMaturityUnit
          annualInterestPercent
          status
          lastUpdatedAt
        }
        value {
          amount
          currencyCode
        }
      }
    }
  }
`);

function hasGrantDetails(asset?: CompanyEquityGrantFields | null) {
  if (!asset) {
    return false;
  }
  const keysToCheck: (keyof CompanyEquityGrantFields)[] = [
    'companyEquityExercisePrice',
    'companyEquityGrantType',
    'companyEquitySharesIssued',
    'companyEquityVestingCliff',
    'companyEquityVestingDuration',
    'companyEquityVestingRate',
    'companyEquityVestingStartDate',
  ];
  return keysToCheck.some((assetKey) => !!asset[assetKey]);
}

export default function AddEditCompanyEquity({
  assetFrag,
  clientUserId,
  onClickBack,
  hiddenFromClient,
  onSuccess,
}: {
  readonly assetFrag?: FragmentType<typeof AddEditCompanyEquityFragment>;
  readonly clientUserId?: string;
  readonly onClickBack?: () => void;
  readonly hiddenFromClient?: boolean;
  readonly onSuccess: Deprecated_HandleItemUpdate;
}) {
  const asset = unmaskFragment(AddEditCompanyEquityFragment, assetFrag);
  const isEditMode = !!asset?.id;
  const [companyEquityAssetFields, setCompanyEquityAssetFields] = useState<AssetFields>({});
  const [flowStep, setFlowStep] = useState(FlowStep.CompanyEquityBasics);
  const [grantDetailsState, setGrantDetailsState] = useState(
    hasGrantDetails(asset) ? GrantDetailsState.YES : GrantDetailsState.UNKNOWN,
  );
  const [createAsset, { error: createError }] = useMutationWithErrorLogging(CREATE_ASSET);
  const [updateAsset, { error: updateError }] = useMutationWithErrorLogging(UPDATE_ASSET);

  async function updateCompanyEquity(updateAssetInput: CompanyEquityUpdateAssetInput) {
    await updateAsset({
      variables: {
        // @ts-expect-error TODO: fix this input type
        input: removeFalsey(updateAssetInput),
      },
    });
    onSuccess({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      id: asset!.id,
      type: UpdateType.UPDATE_COMPANY_EQUITY,
      item: asset,
    });
  }

  async function createCompanyEquity(createAssetInput: CompanyEquityCreateAssetInput) {
    const res = await createAsset({
      variables: {
        // @ts-expect-error TODO: fix this input type
        input: removeFalsey(createAssetInput),
      },
    });
    onSuccess({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      id: res.data!.createAsset!.asset.id,
      type: UpdateType.CREATE_COMPANY_EQUITY,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      item: res.data!.createAsset!.asset,
    });
  }

  switch (flowStep) {
    case FlowStep.CompanyEquityBasics:
      return (
        <>
          {onClickBack && (
            <ItemDialogBackButton
              trackedElementName={isEditMode ? 'edit-company-equity-back-button' : 'add-company-equity-back-button'}
              onClick={onClickBack}
            />
          )}
          <ItemHeader text="Equity details" />
          <CompanyEquityBasicsForm
            assetFrag={asset}
            assetFields={companyEquityAssetFields}
            isEditMode={isEditMode}
            onNext={(assetFields?: AssetFields) => {
              const nextCompanyEquityAssetFields = {
                ...companyEquityAssetFields,
                ...assetFields,
              };
              if (hasGrantDetails({ ...asset, ...nextCompanyEquityAssetFields })) {
                setGrantDetailsState(GrantDetailsState.YES);
              }
              setFlowStep(FlowStep.GrantDetails);
              setCompanyEquityAssetFields(nextCompanyEquityAssetFields);
            }}
          />
        </>
      );
    case FlowStep.GrantDetails:
      return (
        <>
          <ItemDialogBackButton
            trackedElementName={
              isEditMode
                ? 'edit-company-equity-grant-details-back-button'
                : 'add-company-equity-grant-details-back-button'
            }
            onClick={() => {
              setFlowStep(FlowStep.CompanyEquityBasics);
            }}
          />
          <div className="flex flex-col pb-6">
            <ItemHeader text="Do you want to add details of the grant?" />

            <ItemDialogYesNoButtons
              yesButtonElementName="company-equity-grant-details-yes-button"
              noButtonElementName="company-equity-grant-details-no-button"
              value={grantDetailsState.toString()}
              onClick={(value) => {
                const newGrantDetailState = value ? GrantDetailsState.YES : GrantDetailsState.NO;
                setGrantDetailsState(newGrantDetailState);
                if (newGrantDetailState === GrantDetailsState.NO) {
                  setFlowStep(FlowStep.EquityValue);
                }
              }}
            />
          </div>
          {grantDetailsState === GrantDetailsState.YES && (
            <CompanyEquityGrantDetailsForm
              assetFrag={asset}
              assetFields={companyEquityAssetFields}
              isEditMode={isEditMode}
              onNext={(assetFields: AssetFields) => {
                setFlowStep(FlowStep.EquityValue);
                setCompanyEquityAssetFields({
                  ...companyEquityAssetFields,
                  ...assetFields,
                });
              }}
            />
          )}
        </>
      );
    case FlowStep.EquityValue:
      return (
        <>
          <ItemDialogBackButton
            trackedElementName={
              isEditMode ? 'edit-company-equity-value-back-button' : 'add-company-equity-value-back-button'
            }
            onClick={() => {
              setFlowStep(FlowStep.GrantDetails);
            }}
          />
          <ItemHeader text="Equity value" />
          <CompanyEquityValueForm
            assetFrag={asset}
            assetFields={companyEquityAssetFields}
            isEditMode={isEditMode}
            createError={createError}
            updateError={updateError}
            onNext={(assetFields: AssetFields) => {
              const assetPayloadSource: CompanyEquityAssetPayloadSource = {
                ...asset,
                ...companyEquityAssetFields,
                ...assetFields,
              };
              const assetPayload = pick(assetPayloadSource, [
                'description',
                'assetClass',
                'assetSubclass',
                'clientMutationId',
                'companyEquityCompanyName',
                'companyEquityCompanyTicker',
                'companyEquityCurrentPrice',
                'companyEquityExercisePrice',
                'companyEquityGrantType',
                'companyEquitySharesIssued',
                'companyEquityVestingCliff',
                'companyEquityVestingDuration',
                'companyEquityVestingRate',
                'companyEquityVestingStartDate',
                'costBasis',
                'currencyCode',
                'currentValue',
                'id',
              ]);
              if (isEditMode) {
                updateCompanyEquity({
                  ...assetPayload,
                  assetClass: ASSET_CLASS_COMPANY_EQUITY,
                  currencyCode: 'USD',
                  description: 'Company equity',
                });
              } else {
                createCompanyEquity({
                  ...assetPayload,
                  hiddenFromClient,
                  assetClass: ASSET_CLASS_COMPANY_EQUITY,
                  currencyCode: 'USD',
                  description: 'Company equity',
                  userId: clientUserId,
                });
              }
            }}
          />
        </>
      );
  }
}
